import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useState } from 'react';

import { Input } from '@/components/form/Input';
import { useSaveQuery } from '@/sections/insights/queryBuilder/hooks/useSaveQuery';
import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';
import type { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';
import { generateUuid } from '@/utils/uuid.util';

export interface NewQueryModalProps {
  isOpen: boolean;
  onClose: () => void;
  originalQuery?: SavedQuery;
}

/**
 * Modal for creating new queries or duplicating existing queries
 *
 * Modes:
 * - 'new': Creates blank query with empty name/description
 * - 'duplicate': Pre-fills name with "(Duplicate)" suffix and copies description
 *
 * Generates UUID for new query ID (independent of name)
 * Forces folderId to "custom-queries"
 */
export const NewQueryModal = ({
  isOpen,
  onClose,
  originalQuery,
}: NewQueryModalProps) => {
  const [query, setQuery] = useState<SavedQuery>(
    originalQuery || {
      id: generateUuid(),
      name: '',
      description: '',
      type: 'table',
      folderId: 'custom-queries',
      query: {
        node: {
          labels: [],
          filters: [],
          relationships: [],
        },
      },
    }
  );

  const { setMode, setQuery: setGlobalQuery } = useQueryBuilder();

  const { save, isSaving } = useSaveQuery({
    onSuccess: onClose,
  });

  const handleSave = async () => {
    await save(query);

    // Only set query/mode for new queries
    if (!originalQuery) {
      setGlobalQuery(query);
      setMode('split-edit');
    }
  };

  const isNameValid = query.name.trim().length > 0;
  const modalTitle = originalQuery ? 'Duplicate Query' : 'New Query';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent aria-describedby={undefined} className="bg-chariot-background-primary border-chariot-stroke w-full max-w-3xl rounded-lg border p-6">
        <DialogHeader>
          <DialogTitle className="text-chariot-text-primary text-lg font-semibold">
            {modalTitle}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label
              htmlFor="query-name"
              className="text-chariot-text-primary mb-1 block text-sm font-medium"
            >
              Name
            </label>
            <Input
              id="query-name"
              name="query-name"
              value={query.name}
              onChange={e => setQuery({ ...query, name: e.target.value })}
              placeholder="Enter query name"
            />
          </div>

          <div>
            <label
              htmlFor="query-description"
              className="text-chariot-text-primary mb-1 block text-sm font-medium"
            >
              Description
            </label>
            <Textarea
              id="query-description"
              value={query.description}
              onChange={e =>
                setQuery({ ...query, description: e.target.value })
              }
              placeholder="Enter query description"
              rows={6}
            />
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
          >Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={!isNameValid || isSaving}
          >{isSaving ? 'Saving...' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
